import { OmniElement, OmniStyleElement, html } from 'omni-ui';
import CampaignsAPI from '../services/campaigns-api.js';
import SparksHoneyAPI from '../services/sparks-honey.js';

OmniStyleElement.register();

export class CampaignSparksAI extends OmniElement {
  static get properties() {
    return {
      env: { type: String },
      context: { type: Object },
      token: { type: String },
      savedData: { type: Array },
      save: { attribute: false },

      // Internal Properties
      formData: { state: true, type: Object },
      isLoadingStep1: { state: true, type: Boolean },
      isLoading: { state: true, type: Boolean },
    };
  }

  connectedCallback() {
    super.connectedCallback();
    this.campaignsAPI = new CampaignsAPI(this.token, this.env);
    this.formData = structuredClone(this.savedData.form ?? {});
  }

  #dispatchModuleChange = () => {
    const { uuid_module: moduleId } = this.context;
    this.dispatchNewEvent('omni-module-changed', {
      detail: {
        moduleId,
        saveAction: () => this.save({ form: this.formData }),
      },
    });
  };

  #handleChange = e => {
    this.formData[e.target.name ?? e.target.id] = e.target.value;
    this.requestUpdate('formData');
    this.#dispatchModuleChange();
  };

  /**
   * Orchestrates calling the two sparks endpoints sequentially (step 2 depends on the
   * output of step 1). Generates the 3 output fields: cultural trends, shifts, &
   * sparks, and updates this.formData which in turn will rerender the corresponding
   * textareas.
   *
   * @async
   */
  #generate = async () => {
    this.isLoadingStep1 = true;
    this.isLoading = true;
    try {
      const tempToken = (await this.campaignsAPI.qSparksToken()).token;
      const sparksAPI = new SparksHoneyAPI(tempToken, this.env);
      const payload1 = {
        topic: this.formData.category,
        sub_category: this.formData.subcategory,
      };
      const response1 = await sparksAPI.stepOne(payload1);
      const eocs = response1.response.relevant_eocs;
      this.formData.cultural_trends = eocs
        .map(eoc => `${eoc.name}: ${eoc.description}`)
        .join('\n\n');
      this.isLoadingStep1 = false;

      const payload2 = {
        audience: this.formData.audience,
        brand: this.formData.brand,
        business_objective: this.formData.business_objective,
        background: this.formData.background,
        relevant_eocs: eocs,
      };
      const response2 = await sparksAPI.stepTwo(payload2);

      const sparks = [];
      Object.entries(response2.spark).forEach(([, spark]) => {
        spark.value.forEach(v => {
          sparks.push(v.value);
        });
      });
      this.formData.cultural_sparks = sparks.join('\n\n');
      this.formData.cultural_shifts = response2.relevant_eocs
        .map(eoc => eoc.ai_explanation)
        .join('\n\n');
    } finally {
      this.isLoadingStep1 = false;
      this.isLoading = false;
      this.#dispatchModuleChange();
    }
  };

  get #canGenerate() {
    const required = [
      'category',
      'subcategory',
      'audience',
      'brand',
      'business_objective',
      'background',
    ];
    return required.every(key => this.formData[key]?.length);
  }

  get #categories() {
    return [
      'Food and Beverage',
      'Beauty',
      'Health and Wellness',
      'Entertainment and Media',
      'Technology',
      'Auto',
      'Finance and Shopping',
      'Politics and Social movements',
      'Seasonal Holidays',
    ];
  }

  render() {
    return html`
      <omni-style
        @change="${this.#handleChange}"
        @input="${this.#handleChange}"
        >
        <div class="box">
          <p class="title mb-5 is-2">Input Campaign Info</p>
          <p class="is-size-5 mb-3">Identify opportunities to take advantage of cultural shifts in seconds. Fill out the below to get started:</p>
          <div class="columns">
            <div class="column">
              <div class="subtitle mb-2">Category</div>
              <om2-dropdown id="category" .options=${this.#categories}
              .value="${this.formData.category ?? ''}"
              ></om2-dropdown>
            </div>
            <div class="column">
              <div class="subtitle mb-2">Subcategory</div>
              <input class="input" name="subcategory" placeholder="Use natural language to further specify the category. Example: only consider breakfast food" .value="${
                this.formData.subcategory ?? ''
              }"></input>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="subtitle mb-2">Brand</div>
              <textarea class="textarea" name="brand" rows="1" placeholder="Describe the brand here"
              .value="${this.formData.brand ?? ''}"
                ></textarea>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="subtitle mb-2">Business Objective</div>
              <textarea class="textarea" name="business_objective" rows="2" placeholder="Suggested format: We want to GET our audience TO do something BY engaging them with a key message"
              .value="${this.formData.business_objective ?? ''}"
                ></textarea>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="subtitle mb-2">Background</div>
              <textarea class="textarea" name="background" rows="1" placeholder="Add any information on how this brief fits into any wider marketing or campaign strategy"
              .value="${this.formData.background ?? ''}"
              ></textarea>
            </div>
          </div>

          <p class="title mb-5 mt-5 is-2">Define An Audience</p>
          <p class="is-size-5 mb-3">Build an audience insight using the free form field.</p>
          <div class="subtitle mb-2">Audience Description</div>
          <textarea class="textarea" name="audience" rows="1" placeholder="Describe the target audience here"
              .value="${this.formData.audience ?? ''}"
          ></textarea>

          <div class="mt-6 has-text-centered">
            <button class="button is-primary ${
              this.isLoading ? 'is-loading' : ''
            }" ?disabled=${!this.#canGenerate} @click=${
              this.#generate
            }>See Trends and Sparks</button>
          </div>
        </div>

        <div class="box">
          <p class="title mb-5 is-2">Cultural Trends and Sparks</p>
          <p class="is-size-5 mb-3">Below are the cultural trends and shifts most closely aligned with your brief. Choose any trends that relate to cultural sparks - prompts to spark creativity and lean into cultural shifts in positive ways through your brief.</p>

          <div class="columns">
            <div class="column">
              <div class="mb-2"><span class="subtitle">Cultural Trends</span>
                <omni-tooltip class="ml-1">
                  <omni-icon slot="invoker" class="is-size-3 mb-3 is-clickable" icon-id="omni:informative:info"></omni-icon>
                  <div slot="content">Mega/macro-trends impacting your brief</div>
                </omni-tooltip>
              </div>
              <div class="${this.isLoadingStep1 ? 'skeleton' : ''}">
                <textarea class="textarea" name="cultural_trends" .value="${
                  this.formData.cultural_trends ?? ''
                }" ></textarea>
              </div>
            </div>
            <div class="column">
              <div class="mb-2"><span class="subtitle">Cultural Shifts</span>
                <omni-tooltip class="ml-1">
                  <omni-icon slot="invoker" class="is-size-3 mb-3 is-clickable" icon-id="omni:informative:info"></omni-icon>
                  <div slot="content">Cultural changes creating opportunities</div>
                </omni-tooltip>
              </div>
              <div class="${this.isLoading ? 'skeleton' : ''}">
                <textarea class="textarea" name="cultural_shifts" .value="${
                  this.formData.cultural_shifts ?? ''
                }"></textarea>
              </div>
            </div>
          </div>

          <div class="columns">
            <div class="column">
              <div class="mb-2">
                <span class="subtitle">Cultural Sparks</span>
                <omni-tooltip class="ml-1">
                  <omni-icon slot="invoker" class="is-size-3 mb-3 is-clickable" icon-id="omni:informative:info"></omni-icon>
                  <div slot="content">Creative thought starters for actioning the trends</div>
                </omni-tooltip>
              </div>
              <div class="${this.isLoading ? 'skeleton' : ''}">
                <textarea class="textarea" name="cultural_sparks" .value="${
                  this.formData.cultural_sparks ?? ''
                }"></textarea>
              </div>
            </div>
          </div>
        </div>
      </omni-style>
    `;
  }
}

OmniElement.register('campaign-sparks-ai', CampaignSparksAI);
